import React, { useEffect, useState } from 'react';
import { api } from '../api';

/**
 * Drop into Settings → "Connect Desktop Agent" button.
 * Generates a pairing code; rep types it into the Siphon Agent app.
 * Then lists their connected agents.
 */
export default function AgentPairingModal({ onClose }) {
  const [code, setCode] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showTypedFallback, setShowTypedFallback] = useState(false);
  const [directBusy, setDirectBusy] = useState(false);
  const [directError, setDirectError] = useState(null);
  const [error, setError] = useState(null);
  // Tick once a second so the "expires in 4:32" countdown updates live.
  const [, setNow] = useState(Date.now());
  // Installer download info (presigned URLs + last-updated vs this user's last download).
  const [installer, setInstaller] = useState(null);

  useEffect(() => { loadAgents(); loadInstaller(); }, []);

  async function loadInstaller() {
    try { setInstaller(await api.getAgentInstaller()); }
    catch { setInstaller(null); }  // 503 (no build yet / S3 down) → just hide the panel
  }

  async function onDownloaded(arch) {
    try { await api.markAgentDownloaded(arch); } catch { /* non-fatal */ }
    loadInstaller();  // refresh so the "update available" nudge clears
  }

  // Countdown ticker — only runs while a code is showing.
  useEffect(() => {
    if (!code) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [code]);

  const secondsLeft = expiresAt
    ? Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000))
    : null;
  const expired = secondsLeft === 0;

  async function loadAgents() {
    try {
      const list = await api.listMyAgents();
      setAgents(list || []);
      setError(null);
    } catch (e) {
      setError('Failed to load agents: ' + (e.message || e));
    }
  }

  async function genCode() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.createPairingCode();
      setCode(r.pairing_code);
      setExpiresAt(new Date(r.expires_at));
    } catch (e) {
      setError('Failed to generate pairing code: ' + (e.message || e));
    } finally { setBusy(false); }
  }

  // Auto-poll the agent list every 3s while a code is visible. As soon as
  // the rep pairs (server creates an agent_tokens row), the modal swaps to
  // showing it in the "Your connected agents" list — they don't need to
  // click anything.
  useEffect(() => {
    if (!code) return;
    const t = setInterval(loadAgents, 3000);
    return () => clearInterval(t);
  }, [code]);

  async function revoke(id) {
    if (!confirm('Revoke this agent? The desktop app will need to be paired again.')) return;
    try {
      setError(null);
      await api.revokeAgent(id);
      await loadAgents();
    } catch (e) {
      setError('Failed to revoke agent: ' + (e.message || e));
    }
  }

  // One-click pair: mint a token immediately, then launch the desktop app
  // via the `siphon-agent://` URL scheme. The OS routes the URL to the
  // installed app which consumes the token and starts the job loop.
  async function connectThisDevice() {
    setDirectBusy(true);
    setDirectError(null);
    try {
      const r = await api.directPair({ label: '' });   // backend defaults the label
      const launchUrl = `siphon-agent://pair?token=${encodeURIComponent(r.agent_token)}&server=${encodeURIComponent(r.server_url)}`;
      // Trigger the protocol handler. Chrome shows a "Open Siphon Agent?"
      // dialog the first time; users can tick "Always allow" so the second
      // time is silent.
      window.location.href = launchUrl;
      // Poll the agent list for the new entry so the UI confirms success.
      const poll = setInterval(async () => {
        await loadAgents();
      }, 2000);
      setTimeout(() => clearInterval(poll), 30_000);
    } catch (e) {
      setDirectError(e.message || 'Failed to connect this device');
    } finally {
      setDirectBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Connect Desktop Agent</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </div>

        {error && (
          <div className="mb-4 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-200">{error}</div>
        )}

        {/* Step 0: download the installer (presigned, private bucket). Hidden if no build is published. */}
        {installer && (installer.downloads?.arm64 || installer.downloads?.x64) && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 mb-5">
            <p className="text-sm text-slate-300 mb-3">Download the Siphon Agent (macOS):</p>
            <div className="flex flex-wrap gap-2">
              {installer.downloads?.arm64 && (
                <a href={installer.downloads.arm64} onClick={() => onDownloaded('arm64')}
                   className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded text-sm font-medium text-white">
                  Apple Silicon (M-series)
                </a>
              )}
              {installer.downloads?.x64 && (
                <a href={installer.downloads.x64} onClick={() => onDownloaded('x64')}
                   className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm font-medium text-white">
                  Intel
                </a>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-3">
              Latest build: {installer.last_modified ? new Date(installer.last_modified).toLocaleDateString() : '—'}
              {installer.update_available
                ? <span className="text-amber-400"> · Update available — re-download</span>
                : installer.last_download_at
                  ? <span className="text-emerald-400"> · You have the latest</span>
                  : null}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              First launch is blocked by macOS Gatekeeper — right-click the app → <b>Open</b>.
            </div>
          </div>
        )}

        {/* Primary path: one-click connect via the siphon-agent:// URL scheme. */}
        {!showTypedFallback && !code && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 mb-5">
            <p className="text-sm text-slate-300 mb-3">
              The desktop agent runs Playwright on your laptop so scraping uses your real LinkedIn session.
              Make sure the Siphon Agent app is installed, then click below — your browser will hand off a paired token to the app automatically.
            </p>
            {directError && (
              <div className="mb-3 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-200">
                {directError}
              </div>
            )}
            <button
              onClick={connectThisDevice}
              disabled={directBusy}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded text-sm font-medium text-white disabled:opacity-50"
            >
              {directBusy ? 'Connecting…' : 'Connect this device'}
            </button>
            <div className="text-[11px] text-slate-500 mt-3">
              First time? Chrome may ask "Open Siphon Agent?" — tick "Always allow" so future pairs are silent.
              If nothing happens, you haven't installed the agent yet — download it above.
            </div>
            <button
              onClick={() => setShowTypedFallback(true)}
              className="block text-[11px] text-slate-500 hover:text-slate-300 underline mt-3"
            >
              Or pair with a typed code instead
            </button>
          </div>
        )}

        {/* Fallback: typed pairing code. Hidden until the user clicks the link
            or generates a code directly. */}
        {showTypedFallback && !code && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 mb-5">
            <p className="text-sm text-slate-300 mb-3">
              Use this if the "Open Siphon Agent" prompt doesn't fire (custom browsers, locked-down corporate machines, etc.).
              Generate a one-time code, then type it into the agent's window manually.
            </p>
            <button
              onClick={genCode}
              disabled={busy}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-sm font-medium"
            >
              {busy ? '…' : 'Generate pairing code'}
            </button>
            <button
              onClick={() => setShowTypedFallback(false)}
              className="block text-[11px] text-slate-500 hover:text-slate-300 underline mt-3"
            >
              ← Back to one-click pairing
            </button>
          </div>
        )}

        {/* Code-showing block (live during the typed-fallback flow). */}
        {code && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 mb-5">
            <div className="text-xs text-slate-400 mb-2 flex items-center gap-2">
              <span>Pairing code</span>
              {expired ? (
                <span className="text-red-400 font-medium">Expired</span>
              ) : (
                <span className={secondsLeft < 60 ? 'text-amber-400 font-mono tabular-nums' : 'text-slate-500 font-mono tabular-nums'}>
                  expires in {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
                </span>
              )}
            </div>
            <div className={'text-3xl font-mono tracking-[0.3em] mb-3 ' + (expired ? 'text-slate-600 line-through' : 'text-emerald-300')}>
              {code}
            </div>
            {expired ? (
              <button
                onClick={genCode}
                disabled={busy}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-sm font-medium"
              >
                {busy ? '…' : 'Generate a new code'}
              </button>
            ) : (
              <p className="text-xs text-slate-500">
                Open the Siphon Agent app on your laptop, type or paste this code, and hit Pair.
              </p>
            )}
          </div>
        )}

        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Your connected agents</div>
          {agents.length === 0 ? (
            <div className="text-slate-500 text-sm italic">No agents connected yet.</div>
          ) : (
            <div className="space-y-2">
              {agents.map((a) => (
                <div key={a.id} className="bg-slate-800 border border-slate-700 rounded p-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-slate-200">{a.label || 'Desktop agent'}</div>
                    <div className="text-xs text-slate-500">
                      {a.revoked
                        ? <span className="text-rose-400">Revoked</span>
                        : a.last_seen_at
                          ? <>Last seen {new Date(a.last_seen_at).toLocaleString()} · {a.checkpoint_state || 'ok'}</>
                          : 'Never connected'}
                    </div>
                  </div>
                  {!a.revoked && (
                    <button
                      onClick={() => revoke(a.id)}
                      className="text-xs px-3 py-1.5 bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 rounded"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
